import { useState, useEffect } from "react";
import { toast, Toaster } from "sonner";
import { Mail, Phone, MessageCircle, Instagram, Linkedin, ArrowRight, MapPin } from "lucide-react";
import PageHero from "@/components/common/PageHero";
import { IMAGES, BRAND } from "@/data/site";
import { api } from "@/lib/api";

const CONTACT_TYPES = ["Jogador","Familiar","Clube","Academia","Treinador","Agente","Parceiro","Outro"];

const initial = { name: "", email: "", phone: "", country: "", contact_type: "", subject: "", message: "" };

export default function Contacts() {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const j = params.get("jogador");
    if (j) setData((d) => ({ ...d, subject: `Informações sobre ${j}`, contact_type: "Clube" }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contacts", data);
      toast.success("Mensagem enviada com sucesso. Entraremos em contacto assim que possível.");
      setData(initial);
    } catch { toast.error("Erro ao enviar. Tente novamente."); }
    finally { setLoading(false); }
  };

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  return (
    <>
      <Toaster theme="dark" position="top-center" />
      <PageHero
        eyebrow="Contactos"
        title={<>Fale com a <br /><span className="text-[#A67C00]">AF Legacy Sports</span></>}
        subtitle="Estamos disponíveis para responder a jogadores, clubes, academias, treinadores, agentes e parceiros."
        image={IMAGES.contractSigning}
      />

      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16">
          <div className="reveal">
            <div className="section-eyebrow mb-6">Canais diretos</div>
            <h2 className="font-cinzel uppercase text-white text-3xl md:text-4xl tracking-tight mb-10">Contactos disponíveis</h2>

            <ul className="space-y-6 mb-12">
              <li className="flex items-start gap-4 pb-6 border-b border-white/5">
                <Mail size={18} className="text-[#A67C00] mt-1" />
                <div>
                  <div className="section-eyebrow mb-2">Email</div>
                  <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="text-white hover:text-[#A67C00] transition-colors break-all">{BRAND.email}</a>
                </div>
              </li>
              <li className="flex items-start gap-4 pb-6 border-b border-white/5">
                <Phone size={18} className="text-[#A67C00] mt-1" />
                <div>
                  <div className="section-eyebrow mb-2">Telefone</div>
                  <a href={`tel:${BRAND.scouting_phone.replace(/\s/g, "")}`} className="text-white hover:text-[#A67C00] transition-colors">{BRAND.scouting_phone}</a>
                </div>
              </li>
              <li className="flex items-start gap-4 pb-6 border-b border-white/5">
                <MessageCircle size={18} className="text-[#A67C00] mt-1" />
                <div>
                  <div className="section-eyebrow mb-2">WhatsApp</div>
                  <a href={BRAND.socials.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="text-white hover:text-[#A67C00] transition-colors">
                    Enviar mensagem ({BRAND.scouting_phone})
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4 pb-6 border-b border-white/5">
                <MapPin size={18} className="text-[#A67C00] mt-1" />
                <div>
                  <div className="section-eyebrow mb-2">Morada</div>
                  <div className="text-white leading-relaxed">
                    {BRAND.address.line1}<br />
                    {BRAND.address.line2}<br />
                    {BRAND.address.postal}
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-6 pt-2">
                <div className="section-eyebrow">Redes sociais</div>
                <div className="flex items-center gap-4">
                  <a href={BRAND.socials.instagram} target="_blank" rel="noopener noreferrer" data-testid="contacts-instagram" aria-label="Instagram" className="w-10 h-10 border border-white/10 hover:border-[#A67C00] hover:text-[#A67C00] text-white flex items-center justify-center transition-colors"><Instagram size={16} /></a>
                  <a href={BRAND.socials.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contacts-linkedin" aria-label="LinkedIn" className="w-10 h-10 border border-white/10 hover:border-[#A67C00] hover:text-[#A67C00] text-white flex items-center justify-center transition-colors"><Linkedin size={16} /></a>
                  <a href={BRAND.socials.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contacts-whatsapp-icon" aria-label="WhatsApp" className="w-10 h-10 border border-white/10 hover:border-[#A67C00] hover:text-[#A67C00] text-white flex items-center justify-center transition-colors"><MessageCircle size={16} /></a>
                </div>
              </li>
            </ul>
          </div>

          <form data-testid="contact-form" onSubmit={submit} className="reveal">
            <div className="section-eyebrow mb-6">Formulário geral</div>
            <h2 className="font-cinzel uppercase text-white text-3xl md:text-4xl tracking-tight mb-10">Enviar mensagem</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="legacy-label">Nome *</label>
                <input required data-testid="c-name" className="legacy-input" value={data.name} onChange={set("name")} />
              </div>
              <div>
                <label className="legacy-label">Email *</label>
                <input required type="email" data-testid="c-email" className="legacy-input" value={data.email} onChange={set("email")} />
              </div>
              <div>
                <label className="legacy-label">Telefone</label>
                <input data-testid="c-phone" className="legacy-input" value={data.phone} onChange={set("phone")} />
              </div>
              <div>
                <label className="legacy-label">País</label>
                <input data-testid="c-country" className="legacy-input" value={data.country} onChange={set("country")} />
              </div>
              <div className="md:col-span-2">
                <label className="legacy-label">Tipo de contacto</label>
                <select data-testid="c-type" className="legacy-input" value={data.contact_type} onChange={set("contact_type")}>
                  <option value="">Selecionar</option>
                  {CONTACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="legacy-label">Assunto</label>
                <input data-testid="c-subject" className="legacy-input" value={data.subject} onChange={set("subject")} />
              </div>
              <div className="md:col-span-2">
                <label className="legacy-label">Mensagem *</label>
                <textarea required rows={5} data-testid="c-message" className="legacy-input" value={data.message} onChange={set("message")} />
              </div>
            </div>
            <button data-testid="c-submit" disabled={loading} className="btn-gold mt-8">
              {loading ? "A enviar..." : "Enviar mensagem"} <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
